import { useEffect, useMemo, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { textOverride, type UiLayout } from '@forq/platform-data';

/** Eine umbenennbare Navigations-Gruppe: das Gruppen-Label plus die Einträge, je mit Text-Key + Standard. */
export interface NavTermGroup {
  readonly category: string;
  readonly groupKey: string;
  readonly groupLabel: string;
  readonly items: readonly { readonly key: string; readonly label: string }[];
}

interface NavTermsDialogProps {
  readonly open: boolean;
  readonly groups: readonly NavTermGroup[];
  /** Gespeicherte Overrides (zum Vorbefüllen). */
  readonly terms: UiLayout;
  readonly saving: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSave: (overrides: Record<string, string>) => void;
}

/**
 * Dialog zum mandantenweiten Umbenennen der Navigations-Bezeichnungen. Jedes Feld ist mit dem aktuell
 * wirksamen Text vorbefüllt (Override oder Standard); beim Speichern werden NUR echte Abweichungen als
 * Override geschrieben (leer/unverändert = Standard).
 */
export function NavTermsDialog({ open, groups, terms, saving, error, onClose, onSave }: NavTermsDialogProps) {
  const [draft, setDraft] = useState<Record<string, string>>({});

  const defaults = useMemo(() => {
    const map: Record<string, string> = {};
    for (const group of groups) {
      if (group.groupKey) map[group.groupKey] = group.groupLabel;
      for (const item of group.items) map[item.key] = item.label;
    }
    return map;
  }, [groups]);

  // Beim Öffnen den Entwurf aus gespeicherten Overrides + Standards aufbauen.
  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const group of groups) {
      if (group.groupKey) next[group.groupKey] = textOverride(terms, group.groupKey, group.groupLabel);
      for (const item of group.items) next[item.key] = textOverride(terms, item.key, item.label);
    }
    setDraft(next);
  }, [open, groups, terms]);

  if (!open) return null;

  const set = (key: string, value: string) => setDraft((current) => ({ ...current, [key]: value }));

  const save = () => {
    const overrides: Record<string, string> = {};
    for (const [key, value] of Object.entries(draft)) {
      const trimmed = value.trim();
      if (trimmed && trimmed !== defaults[key]) overrides[key] = trimmed;
    }
    onSave(overrides);
  };

  return (
    <div className="forq-terms" role="dialog" aria-modal="true" aria-label="Begriffe umbenennen" onClick={onClose}>
      <div className="forq-terms__panel" onClick={(event) => event.stopPropagation()}>
        <header className="forq-terms__head">
          <div>
            <span className="forq-eyebrow">Mandant · Navigation</span>
            <h2>Begriffe umbenennen</h2>
          </div>
          <button type="button" className="forq-terms__close" onClick={onClose} aria-label="Schließen"><X size={18} /></button>
        </header>
        <p className="forq-terms__lead">Bezeichnungen der Seitenleiste für diesen Mandanten anpassen. Feld leeren = Standard.</p>
        {error ? <div className="forq-terms__error" role="alert">{error}</div> : null}
        <div className="forq-terms__body">
          {groups.map((group) => (
            <section key={group.category} className="forq-terms__group">
              {group.groupKey ? (
                <label className="forq-terms__row is-group">
                  <span className="forq-terms__default">{group.groupLabel}</span>
                  <input value={draft[group.groupKey] ?? ''} placeholder={group.groupLabel} onChange={(event) => set(group.groupKey, event.target.value)} aria-label={`Gruppe ${group.groupLabel}`} />
                </label>
              ) : (
                <div className="forq-terms__row is-group forq-terms__grouphead"><span className="forq-terms__default">{group.groupLabel}</span></div>
              )}
              {group.items.map((item) => (
                <label key={item.key} className="forq-terms__row">
                  <span className="forq-terms__default">{item.label}</span>
                  <input value={draft[item.key] ?? ''} placeholder={item.label} onChange={(event) => set(item.key, event.target.value)} aria-label={item.label} />
                </label>
              ))}
            </section>
          ))}
        </div>
        <footer className="forq-terms__foot">
          <button type="button" className="forq-terms__reset" onClick={() => setDraft({ ...defaults })} disabled={saving}><RotateCcw size={14} /> Alles zurücksetzen</button>
          <div className="forq-terms__foot-actions">
            <button type="button" className="forq-terms__cancel" onClick={onClose} disabled={saving}>Abbrechen</button>
            <button type="button" className="forq-terms__save" onClick={save} disabled={saving}><Check size={14} /> {saving ? 'Speichert …' : 'Speichern'}</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
